import { GetStaticPaths, GetStaticProps } from 'next';
import { LegacyCollectionPageLayout, HeaderText } from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import { TypeCollectionPage } from '@/core/generated-types';
import { defaultCacheRevalidation } from '@/core/contentfulConfig';
import getPathsFromEntries from '@/utils/pathBuilder';
import { LEGACY_CONTENT_TYPE_COLLECTION_PAGE } from '@/core/constants';
import { ShopifyProduct } from '@/lib/shopify';
import { loadLayout } from '@/utils/loadLayout';
import { ContentfulPreviewData, getEntriesByPageType } from '@/lib/contentful';
import { filterContentfulObject } from '@/utils/helpers';

export interface LegacyCollectionPageProps {
  error?: string;
  page: TypeCollectionPage;
  shopifyProducts: (ShopifyProduct | null)[];
}

/**
 * @deprecated Use DynamicCollectionPage instead
 */
const LegacyCollectionPage = ({
  error,
  page,
  shopifyProducts,
}: LegacyCollectionPageProps) => {
  if (error) {
    return (
      <div>
        <HeaderText
          size={HeaderSize.H1}
          text="An Error occurred: "
          hoverEffect
        />
        <HeaderText size={HeaderSize.H2} text={error} hoverEffect />
      </div>
    );
  }

  switch (page.sys.contentType.sys.id) {
    case LEGACY_CONTENT_TYPE_COLLECTION_PAGE.LEGACY_COLLECTION_PAGE:
      return (
        <LegacyCollectionPageLayout
          page={page}
          shopifyProducts={shopifyProducts}
        />
      );
    default:
      console.warn('No matching collection page layout');
      return null;
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  const pageTypes = LEGACY_CONTENT_TYPE_COLLECTION_PAGE;
  const getEntries = await getEntriesByPageType(
    {
      include: 1,
    },
    pageTypes,
  );
  const paths = getPathsFromEntries(getEntries);

  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
  previewData,
}) => {
  const locale = params?.locale as string;
  const previewDataPayload = previewData as ContentfulPreviewData;
  const { globalNav, header, footer, footerLegal } = await loadLayout(
    locale,
    preview,
    previewDataPayload,
  );

  const result = await getEntriesByPageType(
    {
      'fields.slug': params?.slug as string,
      locale,
    },
    LEGACY_CONTENT_TYPE_COLLECTION_PAGE,
    preview,
    previewDataPayload,
  );

  const page = result?.[0].items?.[0] as TypeCollectionPage;

  return {
    notFound: !page,
    props: {
      page: filterContentfulObject(page),
      globalNav,
      header,
      footer,
      footerLegal,
    },
    revalidate: defaultCacheRevalidation,
  };
};

export default LegacyCollectionPage;
